import { createApi } from '@reduxjs/toolkit/query/react';
import { capitalizeString, sortAsc } from '@strata/helper-functions';
import { type FilterCategory } from '@strata/react-filter-pane';
import { type GridWithFilters } from '@strata/react-grid';
import { type ComponentProps } from 'react';

import { USER_SUPPORT_API_URL } from 'api/APIBase';
import { type Conversation } from 'api/userSupportAPI';
import { handleRTKQueryError } from 'services/Error';
import {
    FMH_UPN,
    formatNameMaintainCase,
    mapConversation,
    type MappedConversation,
    type TopicFact,
} from 'services/userSupportHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ConversationTopic {
    name: string;
    value: string;
    shortTitle: string;
}

export interface FillActivityEvent {
    eventId: string;
    eventStatus?: string;
    eventTimestamp: string;
    eventSource: string;
    referralId?: string;
}

export const TagTypes = {
    FILL_ACTIVITY: 'FILL_ACTIVITY',
    CONVERSATION: 'CONVERSATION',
    MESSAGES: 'MESSAGES',
    CONVERSATION_FACTS: 'CONVERSATION_FACTS',
    REFERRAL_CONVERSATIONS: 'REFERRAL_CONVERSATIONS',
    PAGED_CONVERSATIONS: 'PAGED_CONVERSATIONS',
    UNREAD_COUNTS: 'UNREAD_COUNTS',
} as const;

export interface GetConversationsArgs {
    page: number;
    searchText?: string;
    activeFilters?: ComponentProps<typeof GridWithFilters>['activeFilters'];
    sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
}

/**
 * Reduces the grid's active filters to the body the conversations endpoint expects, dropping
 * empty selections. The grid reports "no filters" as both `undefined` and an object of empty
 * arrays, so normalising here keeps equivalent filter states on a single cache entry.
 */
const toConversationFilters = (activeFilters?: GetConversationsArgs['activeFilters']) => {
    const checkLength = (array: string[] = []) => (array.length > 0 ? array : undefined);

    return {
        conversation_status: checkLength(activeFilters?.conversation_status),
        conversation_type: checkLength(activeFilters?.conversation_type),
        supplier_npi: checkLength(activeFilters?.supplier_npi),
        is_unread: checkLength(activeFilters?.is_unread),
        created_by_upn: checkLength(activeFilters?.created_by_upn),
        short_title: checkLength(activeFilters?.short_title),
    };
};

/**
 * The cache key for a page of conversations, derived from the request that actually goes out
 * rather than the raw args. The grid emits several spellings of "no filters" and "no search"
 * while mounting, and they must all land on one cache entry or the page refetches itself.
 * Anything added to the endpoint's `query` must be added here too, or a change to it will
 * reuse the previous cache entry instead of refetching.
 */
export const getConversationsCacheKey = ({
    page,
    searchText,
    activeFilters,
    sort,
}: GetConversationsArgs) =>
    JSON.stringify({
        page,
        // A cleared search box reports '', which is the same query as no search
        searchText: searchText === '' ? undefined : searchText,
        filters: toConversationFilters(activeFilters),
        sort,
    });

export const userSupportApi = createApi({
    baseQuery: getBaseQuery(USER_SUPPORT_API_URL, true),
    tagTypes: Object.values(TagTypes),
    reducerPath: 'userSupportApi',
    endpoints: (build) => ({
        /**
         * Gets a page of conversations for the All Messages grid. Searching, filtering and
         * paging all happen on the backend, so the query args double as the cache key.
         */
        getConversations: build.query<
            { conversations: MappedConversation[]; count: number },
            GetConversationsArgs
        >({
            query: ({ page, searchText, activeFilters, sort }) => ({
                url: '/conversations/list',
                method: 'POST',
                params: {
                    ...(searchText ? { search_term: searchText } : undefined),
                    page,
                },
                body: {
                    additionalFilters: toConversationFilters(activeFilters),
                    sortProperties: sort,
                },
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                `${endpointName}(${getConversationsCacheKey(queryArgs)})`,
            providesTags: (results) => [
                TagTypes.PAGED_CONVERSATIONS,
                ...(results?.conversations ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            ],
            transformResponse: (response: { count: number; conversations: Conversation[] }) => ({
                count: response.count,
                conversations: response.conversations.map(mapConversation),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversations.',
            ),
        }),
        /**
         * Get filtering options for referrals
         * @returns dictionary of distinct values for each column
         */
        getConversationFilterCategories: build.query<FilterCategory<Conversation>[], void>({
            query: () => '/conversations/filter_options',
            transformResponse: (
                response: Record<
                    string,
                    { value: string | boolean; details?: Record<string, string> }[]
                >,
            ): FilterCategory<Conversation>[] => {
                const filterOptions: Record<string, { text: string; value: string | boolean }[]> = {
                    ...response,
                    conversation_status: response.conversation_status.map((item) => ({
                        text: capitalizeString(item.value as string),
                        value: item.value,
                    })),
                    conversation_type: response.conversation_type.map((item) => ({
                        text: capitalizeString(item.value as string),
                        value: item.value,
                    })),
                    is_unread: response.is_unread.map((item) => ({
                        text: item.value ? 'Unread' : 'Read',
                        value: item.value,
                    })),
                    created_by_upn: response.created_by_upn.map(({ value, details }) => ({
                        text: formatNameMaintainCase(
                            details?.created_by_first_name,
                            details?.created_by_last_name,
                        ),
                        value,
                    })),
                };

                return [
                    { key: 'conversation_status', title: 'Conversation Status' },
                    { key: 'is_unread', title: 'Read' },
                    { key: 'created_by_upn', title: 'Created By' },
                    { key: 'short_title', title: 'Conversation Topic' },
                ].map((category) => {
                    let values = filterOptions[category.key]?.sort((a, b) =>
                        sortAsc(a.text ?? a.value, b.text ?? b.value),
                    );
                    if (category.key === 'created_by_upn') {
                        values = [
                            {
                                text: 'Created by Me',
                                value: 'created_by_me',
                            },
                            {
                                text: 'FMH Support',
                                value: FMH_UPN,
                            },
                            ...values,
                        ];
                    }

                    return {
                        ...category,
                        customValues: values,
                    } as FilterCategory<Conversation>;
                });
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversation filter options.',
            ),
        }),
        getConversationTopics: build.query<ConversationTopic[], { anyPerformance?: boolean }>({
            query: () => '/conversations/topics',
            /**
             * Maps conversation topics from the API to conversation topic objects.
             * If anyPerformance is true, the Performance Issue topic will be included.
             */
            transformResponse: (
                response: { topic_id: string; title: string; short_title: string }[],
                _meta,
                { anyPerformance },
            ) =>
                response
                    ?.map((topic) => ({
                        name: topic.title,
                        value: topic.topic_id,
                        shortTitle: topic.short_title,
                    }))
                    .filter(
                        (topic) => !!anyPerformance || topic.shortTitle !== 'Performance Issue',
                    ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving conversation topics.',
            ),
        }),
        getTopicFacts: build.query<Record<string, TopicFact>, { topicId: string }>({
            query: ({ topicId }) => `/conversations/topics/${topicId}/facts`,
            /** Maps and reduces conversation topic facts from the API to conversation topic fact dictionary. */
            transformResponse: (response: TopicFact[]) =>
                response?.reduce<Record<string, TopicFact>>((prev, curr) => {
                    let keyName = curr.key_name;
                    if (prev[keyName]) {
                        keyName = `${keyName}__${curr.order_index}`;
                    }
                    return { ...prev, [keyName]: curr };
                }, {}),
        }),
        getFillActivities: build.query<FillActivityEvent[], { referralId: string }>({
            query: ({ referralId }) => `lifecycle_events/referral/${referralId}`,
            providesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.FILL_ACTIVITY, id: referralId },
            ],
            transformResponse: (
                response: {
                    event_id: string;
                    event_status?: string;
                    raw_status: string;
                    event_timestamp: string;
                    event_source: string;
                }[],
                _meta,
                { referralId },
            ) =>
                response.map((event) => ({
                    referralId,
                    eventId: event.event_id,
                    eventStatus: event.event_status,
                    eventSource: event.event_source,
                    eventTimestamp: event.event_timestamp,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving fill activity for referral.',
            ),
        }),
        postFillActivity: build.mutation<void, { referralId: string; body: { status: string } }>({
            query: ({ referralId, body }) => ({
                url: `lifecycle_events/referral/${referralId}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.FILL_ACTIVITY, id: referralId },
            ],
        }),
        deleteFillActivity: build.mutation<void, { eventId: string; referralId: string }>({
            query: ({ eventId }) => ({
                url: `lifecycle_events/event/${eventId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.FILL_ACTIVITY, id: referralId },
            ],
        }),
    }),
});

export const {
    useGetConversationsQuery,
    useGetConversationFilterCategoriesQuery,
    useGetConversationTopicsQuery,
    useLazyGetTopicFactsQuery,
    useGetFillActivitiesQuery,
    usePostFillActivityMutation,
    useDeleteFillActivityMutation,
} = userSupportApi;
